import { useCallback, useMemo, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { Dropdown, type Option } from '@/UI/Dropdown/Dropdown';
import { FlexCenVer, FlexCenVerSpaceBetween } from '@/UI/styles';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { usePromptView } from '@/components/Recipe/FlowContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { PROMPT_VARIABLE_EVENT_NAMES } from '@/consts/analytics.consts';
import { DisplayMode } from './constants';
import { AddVariableButton } from './AddVariableButton';
import { getTagDisplay } from './utils';
import { insertTag } from './tiptapEditorUtils';
import type { NodeId, PromptDisplayMode } from 'web';
import type { Editor } from '@tiptap/react';
import type { AvailableInput } from './types';

interface PromptNodeFooterProps {
  nodeId: NodeId;
  addInputHandle: () => AvailableInput;
  displayMode: PromptDisplayMode;
  hasInputHandles: boolean;
  editor: Editor;
  editorScrollContainerRef: RefObject<HTMLDivElement | null>;
}

export const PromptNodeFooter = ({
  nodeId,
  addInputHandle,
  displayMode,
  hasInputHandles,
  editor,
  editorScrollContainerRef,
}: PromptNodeFooterProps) => {
  const view = usePromptView(nodeId);
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const wasFocusedRef = useRef(false);

  const handleMouseDown = useCallback(() => {
    // Capture focus state BEFORE the button click removes focus
    wasFocusedRef.current = editor.isFocused;
  }, [editor]);

  const handleAddVariable = useCallback(() => {
    const inputHandle = addInputHandle();
    // Use the focus state captured during mousedown
    const insertAtEnd = !wasFocusedRef.current;
    insertTag({
      editor,
      tagKey: inputHandle.key,
      ...getTagDisplay(inputHandle, displayMode),
      insertAtEnd,
      scrollContainer: editorScrollContainerRef.current,
    });
    track(PROMPT_VARIABLE_EVENT_NAMES.ADD_VARIABLE, { source: 'button' });
  }, [addInputHandle, editor, displayMode, editorScrollContainerRef, track]);

  const displayOptions: Option<PromptDisplayMode>[] = useMemo(
    () => [
      {
        id: DisplayMode.SOURCE_VALUE,
        value: DisplayMode.SOURCE_VALUE,
        label: t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.DISPLAY_SOURCE_VALUE),
      },
      {
        id: DisplayMode.VALUE,
        value: DisplayMode.VALUE,
        label: t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.DISPLAY_VALUE),
      },
      {
        id: DisplayMode.SOURCE,
        value: DisplayMode.SOURCE,
        label: t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.DISPLAY_SOURCE),
      },
    ],
    [t],
  );

  return (
    <FlexCenVerSpaceBetween sx={{ width: '100%', mt: 2 }}>
      <div onMouseDown={handleMouseDown}>
        <AddVariableButton onClick={handleAddVariable} />
      </div>

      {hasInputHandles && (
        <FlexCenVer sx={{ gap: 0.75 }}>
          <Typography variant="body-sm-rg" color={color.White64_T}>
            {t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.DISPLAY_MODE)}
          </Typography>
          <Dropdown
            placement="bottom-start"
            value={displayMode}
            options={displayOptions}
            onChange={(option) => view.setDisplayMode(option.value)}
            size="small"
            width="140px"
          />
        </FlexCenVer>
      )}
    </FlexCenVerSpaceBetween>
  );
};
